"use client"

import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { useEffect, useState } from "react"

export type BarebonesHeroSlide = {
  imageUrl: string
  imageAlt: string
  title: string
  subtitle: string
  linkText: string
  linkHref: string
}

const AUTOPLAY_MS = 5600

const SlideLink = ({ href, children }: { href: string; children: React.ReactNode }) => {
  if (!href) return null
  if (href.startsWith("http")) {
    return (
      <a
        href={href}
        className="inline-flex rounded-full bg-[#f3eadb] px-6 py-3 text-[13px] uppercase tracking-[0.14em] text-[#2b2118] transition-transform duration-300 hover:scale-[1.03]"
      >
        {children}
      </a>
    )
  }
  return (
    <LocalizedClientLink
      href={href}
      className="inline-flex rounded-full bg-[#f3eadb] px-6 py-3 text-[13px] uppercase tracking-[0.14em] text-[#2b2118] transition-transform duration-300 hover:scale-[1.03]"
    >
      {children}
    </LocalizedClientLink>
  )
}

export const BarebonesHero = ({ slides }: { slides: BarebonesHeroSlide[] }) => {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [progressKey, setProgressKey] = useState(0)
  const slideCount = slides.length
  const slide = slides[index]

  useEffect(() => {
    if (slideCount <= 1 || paused) return

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slideCount)
    }, AUTOPLAY_MS)

    return () => window.clearInterval(timer)
  }, [slideCount, paused, index])

  useEffect(() => {
    setProgressKey((key) => key + 1)
  }, [index])

  if (!slide?.imageUrl && !slide?.title) return null

  return (
    <section
      className="relative w-full min-h-[520px] overflow-hidden bg-[#d7d2cb] 2xl:min-h-[640px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
    >
      <div className="absolute inset-0">
        {slides.map((item, slideIndex) => {
          const active = slideIndex === index
          return (
            <div
              key={`${item.imageUrl}-${slideIndex}`}
              className={`absolute inset-0 transition-opacity duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                active ? "z-[1] opacity-100" : "z-0 opacity-0"
              }`}
              aria-hidden={!active}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt=""
                  className={`h-full w-full object-cover will-change-transform ${
                    active ? "bb-hero-kenburns" : "scale-100"
                  }`}
                  style={
                    active
                      ? { animationDuration: `${AUTOPLAY_MS}ms` }
                      : undefined
                  }
                />
              ) : null}
            </div>
          )
        })}
      </div>

      <div
        className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-r from-black/55 via-black/25 to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-28 bg-gradient-to-t from-black/35 to-transparent"
        aria-hidden
      />

      <div className="relative z-10 flex min-h-[520px] max-w-[720px] flex-col justify-center px-10 py-16 text-[#f7f4ef] md:px-16 2xl:min-h-[640px] 2xl:px-24">
        <div key={index}>
          {slide.title && (
            <h1 className="bb-hero-line bb-hero-line-1 bb-serif text-5xl uppercase leading-none tracking-tight md:text-7xl">
              {slide.title}
            </h1>
          )}
          {slide.subtitle && (
            <p className="bb-hero-line bb-hero-line-2 bb-serif mt-4 text-2xl italic text-[#f3eadb]">
              {slide.subtitle}
            </p>
          )}
          {slide.linkText && (
            <div className="bb-hero-line bb-hero-line-3 mt-8">
              <SlideLink href={slide.linkHref}>{slide.linkText}</SlideLink>
            </div>
          )}
        </div>
      </div>

      {slideCount > 1 && (
        <div className="absolute bottom-6 left-10 z-10 flex items-center gap-3 2xl:left-24">
          {slides.map((_, slideIndex) => {
            const active = slideIndex === index
            return (
              <button
                key={slideIndex}
                type="button"
                aria-label={`Slide ${slideIndex + 1}`}
                aria-current={active}
                className={`relative h-[3px] overflow-hidden rounded-full transition-all duration-500 ${
                  active
                    ? "w-10 bg-[#f7f4ef]/25"
                    : "w-2 bg-[#f7f4ef]/40 hover:bg-[#f7f4ef]/70"
                }`}
                onClick={() => setIndex(slideIndex)}
              >
                {active && (
                  <span
                    key={progressKey}
                    className={`absolute inset-y-0 left-0 bg-[#f7f4ef] ${
                      paused ? "w-full" : "bb-hero-progress"
                    }`}
                    style={
                      paused
                        ? undefined
                        : { animationDuration: `${AUTOPLAY_MS}ms` }
                    }
                  />
                )}
              </button>
            )
          })}
        </div>
      )}
    </section>
  )
}
